import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4o1if9btbscp07qbh6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-2xl">
          {/* Left Panel */}
          <div className="md:col-span-2 bg-[#0A1128] p-12 flex flex-col justify-center">
            <div className="space-y-8">
              <div>
                <span className="text-[#00D9FF] text-sm font-semibold tracking-[0.2em] uppercase">GET IN TOUCH</span>
                <h2 className="text-3xl font-bold text-white mt-4 mb-8">Contact Information</h2>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-[#00D9FF]"></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Email</div>
                    <a href="mailto:support@impormanzsas.com" className="text-white text-lg hover:text-[#00D9FF] transition-colors duration-300">
                      support@impormanzsas.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-[#00D9FF]"></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Phone</div>
                    <a href="tel:+18557746912" className="text-white text-lg hover:text-[#00D9FF] transition-colors duration-300">
                      +1 855 774 6912
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-[#00D9FF]"></i>
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Address</div>
                    <div className="text-white text-lg leading-relaxed">
                      VIA MAGDALENA KM 14<br />
                      MANIZALES, 170001<br />
                      Colombia
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Form */}
          <div className="md:col-span-3 bg-white p-12">
            <h3 className="text-3xl font-bold text-[#0A1128] mb-8">Send us a message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#00D9FF] focus:outline-none transition-colors duration-300 text-sm"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#00D9FF] focus:outline-none transition-colors duration-300 text-sm"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#00D9FF] focus:outline-none transition-colors duration-300 text-sm"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, message: e.target.value });
                    }
                  }}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#00D9FF] focus:outline-none transition-colors duration-300 resize-none text-sm"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl">
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
                  Something went wrong. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#00D9FF] to-[#8B5CF6] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-[#00D9FF]/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
